import {
    type CSSProperties,
    useEffect,
    useState,
    useRef
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    type MessageScrollerScrollPosition,
    type MessageScrollerStackBehaviour,
    NCoreUIKitTheme,
    MessageScroller,
    ToggleGroup,
    Composer,
    Message,
    Button,
    Bubble,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    InlineCode,
    BulletList,
    Paragraph,
    CodeBlock,
    DemoFrame,
    Article,
    Preview,
    Section
} from "../../components/content";

type Turn = {
    isUser: boolean;
    text: string;
    id: string;
};

const NAME = "MessageScroller";

const USAGE_CODE = `<MessageScroller.Provider
    defaultScrollPosition="last-anchor"
    stackBehaviour="top"
>
    <MessageScroller
        title="Conversation"
    >
        <MessageScroller.Viewport>
            <MessageScroller.Content
                isBusy={isReplying}
            >
                {
                    turns.map((turn) => <MessageScroller.Item
                        isScrollAnchor={turn.isUser}
                        messageId={turn.id}
                        key={turn.id}
                    >
                        <Message
                            align={turn.isUser ? "end" : "start"}
                        >
                            <Bubble
                                variant={turn.isUser ? "user" : "assistant"}
                            >
                                {turn.text}
                            </Bubble>
                        </Message>
                    </MessageScroller.Item>)
                }
            </MessageScroller.Content>
        </MessageScroller.Viewport>
        <MessageScroller.Button />
        <MessageScroller.Footer>
            <Composer
                onSubmit={send}
            />
        </MessageScroller.Footer>
    </MessageScroller>
</MessageScroller.Provider>`;

const ANCHOR_CODE = `<MessageScroller.Provider
    defaultScrollPosition="first-unread"
    scrollPreviousItemPeek={48}
    stackBehaviour="top"
    isAutoScroll={true}
>
    ...
</MessageScroller.Provider>

<MessageScroller.Item
    isScrollAnchor={turn.isUser}
    isUnread={turn.isUnread}
    messageId={turn.id}
>
    ...
</MessageScroller.Item>`;

const OLDER_CODE = `const loadOlder = async () => {
    const page = await fetchOlder(turns[0].id);

    setTurns((current) => [...page, ...current]);

    setHasMore(page.length > 0);
};

<MessageScroller.Provider
    hasOlderMessages={hasMore}
    defaultScrollPosition="end"
    onLoadOlder={loadOlder}
>
    ...
</MessageScroller.Provider>`;

const JUMP_CODE = `const {
    currentAnchorId,
    newMessageCount,
    scrollToMessage,
    scrollToStart,
    scrollToEnd,
    isAtStart,
    isAtEnd
} = MessageScroller.useContext();

scrollToMessage("turn-1", {
    isHighlighted: true
});

<MessageScroller.Button
    direction="start"
/>
<MessageScroller.Button />`;

const LOADING_CODE = `<MessageScroller.Provider
    hasOlderMessages={false}
    isLoading={isLoading}
>
    ...
</MessageScroller.Provider>`;

const SEED_KEYS = [
    "messageScrollerPage.turn1",
    "messageScrollerPage.turn2",
    "messageScrollerPage.turn3",
    "messageScrollerPage.turn4",
    "messageScrollerPage.turn5",
    "messageScrollerPage.turn6"
] as const;

const STACK_BEHAVIOURS: Array<MessageScrollerStackBehaviour> = [
    "top",
    "bottom"
];

const POSITIONS: Array<MessageScrollerScrollPosition> = [
    "last-anchor",
    "first-unread",
    "end",
    "start"
];

const CONTROLS_STYLE: CSSProperties = {
    flexDirection: "column",
    display: "flex"
};

const BUTTONS_STYLE: CSSProperties = {
    flexWrap: "wrap",
    display: "flex"
};

const REPLY_DELAY = 1200;

const LOAD_DELAY = 900;

const LOADING_DELAY = 1600;

const OLDER_BATCH_SIZE = 3;

const MAX_BATCHES = 2;

const UNREAD_FROM = 2;

const getSeedTurns = (t: DocsTranslate): Array<Turn> => SEED_KEYS.map((key, index) => ({
    isUser: index % 2 === 1,
    id: `seed${index}`,
    text: t(key)
}));

const TurnItem = ({
    isUnread = false,
    turn
}: {
    isUnread?: boolean;
    turn: Turn;
}) => <MessageScroller.Item
    isScrollAnchor={turn.isUser}
    messageId={turn.id}
    isUnread={isUnread}
>
    <Message
        align={turn.isUser ? "end" : "start"}
    >
        <Bubble
            variant={turn.isUser ? "user" : "assistant"}
        >
            {turn.text}
        </Bubble>
    </Message>
</MessageScroller.Item>;

const ConversationDemo = () => {
    const docsTokens = NCoreUIKitTheme.useContext();

    const {
        t
    } = useDocsI18n();

    const [
        added,
        setAdded
    ] = useState<Array<Turn>>([]);

    const [
        isReplying,
        setIsReplying
    ] = useState(false);

    useEffect(() => {
        if(!isReplying) {
            return;
        }

        const timer = window.setTimeout(() => {
            setAdded((current) => [
                ...current,
                {
                    text: t("messageScrollerPage.reply"),
                    id: `reply${current.length}`,
                    isUser: false
                }
            ]);

            setIsReplying(false);
        }, REPLY_DELAY);

        return () => {
            window.clearTimeout(timer);
        };
    }, [
        isReplying
    ]);

    const handleSubmit = (text: string) => {
        setAdded((current) => [
            ...current,
            {
                id: `user${current.length}`,
                isUser: true,
                text
            }
        ]);

        setIsReplying(true);
    };

    const handleStop = () => setIsReplying(false);

    const turns = [
        ...getSeedTurns(t),
        ...added
    ];

    return <DemoFrame>
        <MessageScroller.Provider>
            <MessageScroller
                style={{
                    height: docsTokens.shapeConfigs.chartHeight.large
                }}
                title={t("messageScrollerPage.title")}
            >
                <MessageScroller.Viewport>
                    <MessageScroller.Content
                        isBusy={isReplying}
                    >
                        {
                            turns.map((turn) => <TurnItem
                                key={turn.id}
                                turn={turn}
                            />)
                        }
                        {
                            isReplying ?
                                <MessageScroller.Item
                                    messageId="typing"
                                >
                                    <Message>
                                        <Bubble>
                                            <Bubble.Typing />
                                        </Bubble>
                                    </Message>
                                </MessageScroller.Item>
                            :
                                null
                        }
                    </MessageScroller.Content>
                </MessageScroller.Viewport>
                <MessageScroller.Button />
                <MessageScroller.Footer>
                    <Composer
                        onStopPress={handleStop}
                        isStreaming={isReplying}
                        onSubmit={handleSubmit}
                    />
                </MessageScroller.Footer>
            </MessageScroller>
        </MessageScroller.Provider>
    </DemoFrame>;
};

const PositionDemo = ({
    position
}: {
    position: MessageScrollerScrollPosition;
}) => {
    const docsTokens = NCoreUIKitTheme.useContext();

    const {
        t
    } = useDocsI18n();

    const turns: Array<Turn> = [
        ...getSeedTurns(t),
        {
            text: t("messageScrollerPage.answerOne"),
            id: "answer1",
            isUser: false
        },
        {
            text: t("messageScrollerPage.answerTwo"),
            id: "answer2",
            isUser: false
        }
    ];

    return <DemoFrame>
        <MessageScroller.Provider
            defaultScrollPosition={position}
            key={position}
        >
            <MessageScroller
                style={{
                    height: docsTokens.shapeConfigs.chartHeight.medium
                }}
                title={t("messageScrollerPage.title")}
            >
                <MessageScroller.Viewport>
                    <MessageScroller.Content>
                        {
                            turns.map((turn, index) => <TurnItem
                                isUnread={index >= UNREAD_FROM}
                                key={turn.id}
                                turn={turn}
                            />)
                        }
                    </MessageScroller.Content>
                </MessageScroller.Viewport>
            </MessageScroller>
        </MessageScroller.Provider>
    </DemoFrame>;
};

const StackDemo = ({
    stackBehaviour
}: {
    stackBehaviour: MessageScrollerStackBehaviour;
}) => {
    const docsTokens = NCoreUIKitTheme.useContext();

    const {
        t
    } = useDocsI18n();

    return <DemoFrame>
        <MessageScroller.Provider
            stackBehaviour={stackBehaviour}
        >
            <MessageScroller
                style={{
                    height: docsTokens.shapeConfigs.chartHeight.medium
                }}
                title={t("messageScrollerPage.title")}
            >
                <MessageScroller.Viewport>
                    <MessageScroller.Content>
                        {
                            getSeedTurns(t).slice(0, 2).map((turn) => <TurnItem
                                key={turn.id}
                                turn={turn}
                            />)
                        }
                    </MessageScroller.Content>
                </MessageScroller.Viewport>
            </MessageScroller>
        </MessageScroller.Provider>
    </DemoFrame>;
};

const OlderDemo = () => {
    const docsTokens = NCoreUIKitTheme.useContext();

    const {
        t
    } = useDocsI18n();

    const [
        batches,
        setBatches
    ] = useState(0);

    const timerRef = useRef<number | undefined>(undefined);

    useEffect(() => {
        return () => {
            window.clearTimeout(timerRef.current);
        };
    }, []);

    const handleLoadOlder = () => new Promise<void>((resolve) => {
        timerRef.current = window.setTimeout(() => {
            setBatches((current) => current + 1);

            resolve();
        }, LOAD_DELAY);
    });

    const older: Array<Turn> = Array.from({
        length: batches * OLDER_BATCH_SIZE
    }, (_, index) => {
        const number = batches * OLDER_BATCH_SIZE - index;

        return {
            text: t("messageScrollerPage.olderTurn", [
                number
            ]),
            isUser: number % 2 === 0,
            id: `older${number}`
        };
    });

    const turns = [
        ...older,
        ...getSeedTurns(t)
    ];

    return <DemoFrame>
        <MessageScroller.Provider
            hasOlderMessages={batches < MAX_BATCHES}
            onLoadOlder={handleLoadOlder}
            defaultScrollPosition="end"
        >
            <MessageScroller
                style={{
                    height: docsTokens.shapeConfigs.chartHeight.large
                }}
                title={t("messageScrollerPage.title")}
            >
                <MessageScroller.Viewport>
                    <MessageScroller.Content>
                        {
                            turns.map((turn) => <TurnItem
                                key={turn.id}
                                turn={turn}
                            />)
                        }
                    </MessageScroller.Content>
                </MessageScroller.Viewport>
            </MessageScroller>
        </MessageScroller.Provider>
    </DemoFrame>;
};

const JumpControls = () => {
    const {
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        t
    } = useDocsI18n();

    const {
        currentAnchorId,
        scrollToMessage,
        scrollToStart,
        scrollToEnd,
        isAtStart,
        isAtEnd
    } = MessageScroller.useContext();

    const handleFirst = () => scrollToMessage("seed0", {
        isHighlighted: true
    });

    return <div
        style={{
            ...CONTROLS_STYLE,
            gap: spaces.spacingSm
        }}
    >
        <div
            style={{
                ...BUTTONS_STYLE,
                gap: spaces.spacingSm
            }}
        >
            <Button
                title={t("messageScrollerPage.jumpFirst")}
                onPress={handleFirst}
                variant="outline"
                type="neutral"
                size="small"
            />
            <Button
                title={t("messageScrollerPage.jumpStart")}
                onPress={scrollToStart}
                variant="outline"
                type="neutral"
                size="small"
            />
            <Button
                title={t("messageScrollerPage.jumpEnd")}
                onPress={scrollToEnd}
                variant="outline"
                type="neutral"
                size="small"
            />
        </div>
        <Text
            variant="monoMediumSize"
            color="low"
        >
            {`isAtStart: ${isAtStart} · isAtEnd: ${isAtEnd} · currentAnchorId: ${currentAnchorId ?? "null"}`}
        </Text>
    </div>;
};

const JumpDemo = () => {
    const docsTokens = NCoreUIKitTheme.useContext();

    const {
        t
    } = useDocsI18n();

    return <DemoFrame>
        <MessageScroller.Provider
            defaultScrollPosition="start"
        >
            <MessageScroller
                style={{
                    height: docsTokens.shapeConfigs.chartHeight.large
                }}
                title={t("messageScrollerPage.title")}
            >
                <MessageScroller.Viewport>
                    <MessageScroller.Content>
                        {
                            getSeedTurns(t).map((turn) => <TurnItem
                                key={turn.id}
                                turn={turn}
                            />)
                        }
                    </MessageScroller.Content>
                </MessageScroller.Viewport>
                <MessageScroller.Button
                    direction="start"
                />
                <MessageScroller.Button />
                <MessageScroller.Footer>
                    <JumpControls />
                </MessageScroller.Footer>
            </MessageScroller>
        </MessageScroller.Provider>
    </DemoFrame>;
};

const LoadingDemo = () => {
    const docsTokens = NCoreUIKitTheme.useContext();

    const {
        t
    } = useDocsI18n();

    const [
        isLoading,
        setIsLoading
    ] = useState(false);

    useEffect(() => {
        if(!isLoading) {
            return;
        }

        const timer = window.setTimeout(() => setIsLoading(false), LOADING_DELAY);

        return () => {
            window.clearTimeout(timer);
        };
    }, [
        isLoading
    ]);

    const handleReload = () => setIsLoading(true);

    return <>
        <DemoFrame>
            <MessageScroller.Provider
                hasOlderMessages={false}
                isLoading={isLoading}
            >
                <MessageScroller
                    style={{
                        height: docsTokens.shapeConfigs.chartHeight.medium
                    }}
                    title={t("messageScrollerPage.title")}
                >
                    <MessageScroller.Viewport>
                        <MessageScroller.Content>
                            {
                                getSeedTurns(t).slice(0, 2).map((turn) => <TurnItem
                                    key={turn.id}
                                    turn={turn}
                                />)
                            }
                        </MessageScroller.Content>
                    </MessageScroller.Viewport>
                </MessageScroller>
            </MessageScroller.Provider>
        </DemoFrame>
        <Button
            title={t("messageScrollerPage.reload")}
            onPress={handleReload}
            isDisabled={isLoading}
            variant="outline"
            type="neutral"
            size="small"
        />
    </>;
};

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("messageScrollerPage.anchorTitle"),
        id: "anchor"
    },
    {
        title: t("messageScrollerPage.olderTitle"),
        id: "older"
    },
    {
        title: t("messageScrollerPage.jumpTitle"),
        id: "jump"
    },
    {
        title: t("messageScrollerPage.loadingTitle"),
        id: "loading"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const MessageScrollerPage = () => {
    const {
        t
    } = useDocsI18n();

    const [
        position,
        setPosition
    ] = useState<MessageScrollerScrollPosition>("last-anchor");

    const [
        stackBehaviour,
        setStackBehaviour
    ] = useState<MessageScrollerStackBehaviour>("top");

    const handlePositionChange = (value: string | Array<string>) => {
        const next = POSITIONS.find((item) => item === value);

        if(next) {
            setPosition(next);
        }
    };

    const handleStackChange = (value: string | Array<string>) => {
        const next = STACK_BEHAVIOURS.find((item) => item === value);

        if(next) {
            setStackBehaviour(next);
        }
    };

    return <Article>
        <ComponentHeader
            lead={t("messageScrollerPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("messageScrollerPage.overviewText")}
            </Paragraph>
            <Paragraph>
                {t("messageScrollerPage.overviewAlternatives")}
            </Paragraph>
            <Preview
                caption={t("messageScrollerPage.overviewCaption")}
                isStacked={true}
            >
                <ConversationDemo />
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("messageScrollerPage.anchorTitle")}
            id="anchor"
        >
            <Paragraph>
                {t("messageScrollerPage.anchorText")}
            </Paragraph>
            <Paragraph>
                {t("messageScrollerPage.positionText")}
            </Paragraph>
            <Preview
                caption={t("messageScrollerPage.positionCaption")}
                isStacked={true}
            >
                <ToggleGroup
                    title={t("messageScrollerPage.positionLabel")}
                    onValueChange={handlePositionChange}
                    attachBehaviour="attached"
                    isClearEnabled={false}
                    variant="outline"
                    value={position}
                    size="small"
                >
                    {
                        POSITIONS.map((item) => <ToggleGroup.Item
                            title={item}
                            value={item}
                            key={item}
                        />)
                    }
                </ToggleGroup>
                <PositionDemo
                    position={position}
                />
            </Preview>
            <Paragraph>
                {t("messageScrollerPage.stackText")}
            </Paragraph>
            <Preview
                caption={t("messageScrollerPage.stackCaption")}
                isStacked={true}
            >
                <ToggleGroup
                    title={t("messageScrollerPage.stackBehaviour")}
                    onValueChange={handleStackChange}
                    attachBehaviour="attached"
                    value={stackBehaviour}
                    isClearEnabled={false}
                    variant="outline"
                    size="small"
                >
                    {
                        STACK_BEHAVIOURS.map((item) => <ToggleGroup.Item
                            title={item}
                            value={item}
                            key={item}
                        />)
                    }
                </ToggleGroup>
                <StackDemo
                    stackBehaviour={stackBehaviour}
                />
            </Preview>
            <CodeBlock
                code={ANCHOR_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("messageScrollerPage.olderTitle")}
            id="older"
        >
            <Paragraph>
                {t("messageScrollerPage.olderText")}
            </Paragraph>
            <Preview
                caption={t("messageScrollerPage.olderCaption")}
                isStacked={true}
            >
                <OlderDemo />
            </Preview>
            <CodeBlock
                code={OLDER_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("messageScrollerPage.jumpTitle")}
            id="jump"
        >
            <Paragraph>
                {t("messageScrollerPage.jumpText")}
            </Paragraph>
            <Preview
                caption={t("messageScrollerPage.jumpCaption")}
                isStacked={true}
            >
                <JumpDemo />
            </Preview>
            <CodeBlock
                code={JUMP_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("messageScrollerPage.loadingTitle")}
            id="loading"
        >
            <Paragraph>
                {t("messageScrollerPage.loadingText")}
            </Paragraph>
            <Preview
                caption={t("messageScrollerPage.loadingCaption")}
                isStacked={true}
            >
                <LoadingDemo />
            </Preview>
            <CodeBlock
                code={LOADING_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("messageScrollerPage.a11yViewport"),
                    t("messageScrollerPage.a11yLog"),
                    t("messageScrollerPage.a11yKeyboard"),
                    t("messageScrollerPage.a11yJump"),
                    t("messageScrollerPage.a11yButton"),
                    t("messageScrollerPage.a11yLoader"),
                    t("messageScrollerPage.a11yMotion"),
                    t("messageScrollerPage.a11yParts")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <Paragraph>
                <InlineCode>
                    MessageScroller
                </InlineCode>
                {" "}
                {t("messageScrollerPage.rootPropsText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("messageScrollerPage.rootTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("messageScrollerPage.rootChildren"),
                        type: "ReactNode",
                        name: "children"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("messageScrollerPage.rootCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("messageScrollerPage.rootCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("messageScrollerPage.propsNote")}
            </Paragraph>
            <Paragraph>
                <InlineCode>
                    MessageScroller.Provider
                </InlineCode>
                {" "}
                {t("messageScrollerPage.providerPropsText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("messageScrollerPage.providerDefaultScrollPosition"),
                        type: "\"last-anchor\" | \"first-unread\" | \"end\" | \"start\"",
                        defaultValue: "\"last-anchor\"",
                        name: "defaultScrollPosition"
                    },
                    {
                        description: t("messageScrollerPage.providerStackBehaviour"),
                        type: "\"top\" | \"bottom\"",
                        defaultValue: "\"top\"",
                        name: "stackBehaviour"
                    },
                    {
                        description: t("messageScrollerPage.providerIsAutoScroll"),
                        defaultValue: "true",
                        name: "isAutoScroll",
                        type: "boolean"
                    },
                    {
                        description: t("messageScrollerPage.providerPeek"),
                        name: "scrollPreviousItemPeek",
                        type: "number"
                    },
                    {
                        description: t("messageScrollerPage.providerHasOlder"),
                        name: "hasOlderMessages",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("messageScrollerPage.providerOnLoadOlder"),
                        type: "() => Promise<void>",
                        name: "onLoadOlder"
                    },
                    {
                        description: t("messageScrollerPage.providerIsPreserve"),
                        name: "isPreserveScrollOnPrepend",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("messageScrollerPage.providerIsLoading"),
                        defaultValue: "false",
                        name: "isLoading",
                        type: "boolean"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("messageScrollerPage.providerCustomTheme"),
                        name: "customTheme"
                    }
                ]}
            />
            <Paragraph>
                <InlineCode>
                    MessageScroller.Item
                </InlineCode>
                {" "}
                {t("messageScrollerPage.itemPropsText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("messageScrollerPage.itemMessageId"),
                        name: "messageId",
                        type: "string"
                    },
                    {
                        description: t("messageScrollerPage.itemIsScrollAnchor"),
                        name: "isScrollAnchor",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("messageScrollerPage.itemIsUnread"),
                        defaultValue: "false",
                        name: "isUnread",
                        type: "boolean"
                    },
                    {
                        description: t("messageScrollerPage.itemChildren"),
                        type: "ReactNode",
                        name: "children"
                    }
                ]}
            />
            <Paragraph>
                <InlineCode>
                    MessageScroller.Content
                </InlineCode>
                {" "}
                {t("messageScrollerPage.contentPropsText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("messageScrollerPage.contentIsBusy"),
                        name: "isBusy",
                        type: "boolean"
                    }
                ]}
            />
            <Paragraph>
                <InlineCode>
                    MessageScroller.Button
                </InlineCode>
                {" "}
                {t("messageScrollerPage.buttonPropsText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("messageScrollerPage.buttonDirection"),
                        type: "\"end\" | \"start\"",
                        defaultValue: "\"end\"",
                        name: "direction"
                    },
                    {
                        description: t("messageScrollerPage.buttonNewMessageCount"),
                        name: "newMessageCount",
                        type: "number"
                    },
                    {
                        description: t("messageScrollerPage.buttonCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
            <Paragraph>
                {t("messageScrollerPage.hooksPropsText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        type: "(id: string, options?: { isHighlighted?: boolean }) => boolean",
                        description: t("messageScrollerPage.scrollToMessage"),
                        name: "MessageScroller.useContext().scrollToMessage"
                    },
                    {
                        description: t("messageScrollerPage.scrollToStart"),
                        name: "MessageScroller.useContext().scrollToStart",
                        type: "() => void"
                    },
                    {
                        description: t("messageScrollerPage.scrollToEnd"),
                        name: "MessageScroller.useContext().scrollToEnd",
                        type: "() => void"
                    },
                    {
                        type: "{ currentAnchorId: string | null; newMessageCount: number; isAtStart: boolean; isAtEnd: boolean }",
                        description: t("messageScrollerPage.visibility"),
                        name: "MessageScroller.useContext()"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("messageScrollerPage.partsNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default MessageScrollerPage;
